import { useMemo } from "react";
import { useOrganization } from "@clerk/react-router";

export interface UseClientAdminResult {
    /** Whether the current user is a ClientAdmin (org:admin in any non-ACOM org) */
    isClientAdmin: boolean;
    /** Whether Clerk organization data has loaded */
    isLoaded: boolean;
}

/**
 * Hook to determine if the current user is a ClientAdmin.
 *
 * Checks two conditions:
 * 1. The Clerk organization name is NOT "ACOM" (case-insensitive)
 * 2. The user's org membership role is "org:admin"
 *
 * Mutually exclusive with useAcomAdmin by design —
 * an ACOM admin's org name is "ACOM", so they can never satisfy condition 1.
 *
 * This is a UI-only check for routing/feature visibility.
 * The backend is the authoritative auth check.
 */
export function useClientAdmin(): UseClientAdminResult {
    const { organization, membership, isLoaded } = useOrganization();

    const isClientAdmin = useMemo(() => {
        if (!isLoaded || !organization || !membership) return false;

        const isNonAcomOrg = organization.name?.toUpperCase() !== "ACOM";
        const isAdmin = membership.role === "org:admin";

        return isNonAcomOrg && isAdmin;
    }, [isLoaded, organization, membership]);

    return { isClientAdmin, isLoaded };
}
